<template>
  <div class="p-4 space-y-4">
    <LeaveAdminSectionNav />

    <el-card shadow="hover" class="rounded-2xl border border-gray-200">
      <div class="text-base font-semibold mb-4">신청내역 · 증빙 서류</div>

      <div class="flex flex-wrap items-center gap-3 mb-3">
        <el-input-number v-model="filters.year" :min="2020" :max="2100" style="width: 130px" />
        <el-select v-model="filters.leave_type" clearable placeholder="휴가유형" style="width: 150px">
          <el-option v-for="t in leaveTypes" :key="t.id" :label="t.name" :value="t.id" />
        </el-select>
        <el-select v-model="filters.approval_status" clearable placeholder="상태" style="width: 120px">
          <el-option label="승인" value="approved" />
          <el-option label="대기" value="pending" />
          <el-option label="반려" value="rejected" />
        </el-select>
        <el-select v-model="filters.has_attachment" clearable placeholder="증빙 유무" style="width: 130px">
          <el-option label="첨부 있음" value="1" />
          <el-option label="첨부 없음" value="0" />
        </el-select>
        <el-checkbox v-model="filters.requires_attachment_only">증빙 필수 유형만</el-checkbox>
        <el-input v-model="filters.search" clearable placeholder="직원/사유 검색" style="width: 220px" />
        <el-button type="primary" @click="fetchRows" :loading="loading">조회</el-button>
      </div>

      <div class="text-xs text-gray-500 mb-2">
        총 {{ rows.length }}건
        <span v-if="missingProofCount > 0" class="missing-warn">
          · 증빙 필수인데 미첨부 {{ missingProofCount }}건
        </span>
      </div>

      <el-table :data="rows || []" size="small" style="width: 100%" max-height="560" v-loading="loading">
        <el-table-column prop="start_date" label="시작일" width="110" />
        <el-table-column prop="end_date" label="종료일" width="110" />
        <el-table-column prop="employee_name" label="직원" width="100" />
        <el-table-column prop="department_name" label="부서" width="120" />
        <el-table-column prop="leave_type_name" label="휴가유형" width="110" />
        <el-table-column prop="days_requested" label="일수" width="70" align="right" />
        <el-table-column label="상태" width="90">
          <template #default="{ row }">
            <el-tag :type="statusTagType(row.approval_status)" size="small">
              {{ statusLabel(row.approval_status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="증빙 서류" min-width="260">
          <template #default="{ row }">
            <div v-if="row.attachments && row.attachments.length" class="att-links">
              <a
                v-for="att in row.attachments"
                :key="att.id"
                :href="att.file_url || att.file"
                target="_blank"
                rel="noopener"
                class="att-link"
              >📎 {{ att.original_name || '첨부파일' }}</a>
            </div>
            <!-- 증빙 필수 유형인데 첨부가 없으면 인사담당자가 바로 알아야 한다 -->
            <el-tag v-else-if="isProofRequired(row)" type="danger" effect="light" size="small">미첨부</el-tag>
            <span v-else style="color:#c0c4cc;">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="reason" label="사유" min-width="180" show-overflow-tooltip />
        <el-table-column prop="approved_by_name" label="승인자" width="100" />
        <!-- 관리자 정정 (2026-08-03) — 어드민 직접 수정 대체. 정정관리자 그룹만. -->
        <el-table-column v-if="canCorrect" label="정정" width="70" fixed="right">
          <template #default="{ row }">
            <el-button size="small" type="warning" plain @click="openCorrection(row)">정정</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 관리자 정정 다이얼로그 -->
    <el-dialog v-model="correction.visible" title="휴가 관리자 정정" width="480px">
      <div v-if="correction.row" class="space-y-3">
        <div class="text-sm text-gray-600 mb-2">
          {{ correction.row.employee_name }} · {{ correction.row.leave_type_name }}
          ({{ statusLabel(correction.row.approval_status) }})
        </div>
        <el-alert type="warning" :closable="false" show-icon class="mb-3"
          title="승인 완료 건도 수정됩니다. 잔여 일수는 자동 재계산되고, 정정 내용과 사유는 감사이력에 기록됩니다." />

        <el-radio-group v-model="correction.mode" class="mb-2">
          <el-radio value="edit">내용 수정</el-radio>
          <el-radio value="cancel">휴가 취소 (잘못 올라간 건)</el-radio>
        </el-radio-group>

        <template v-if="correction.mode === 'edit'">
          <div class="corr-grid">
            <label>시작일</label>
            <el-date-picker v-model="correction.form.start_date" type="date"
              value-format="YYYY-MM-DD" style="width: 100%" />
            <label>종료일</label>
            <el-date-picker v-model="correction.form.end_date" type="date"
              value-format="YYYY-MM-DD" style="width: 100%" />
            <label>일수</label>
            <el-input-number v-model="correction.form.days_requested" :min="0.5" :step="0.5" style="width: 100%" />
            <label>시간</label>
            <el-select v-model="correction.form.session" style="width: 100%">
              <el-option label="하루" value="full" />
              <el-option label="오전" value="morning" />
              <el-option label="오후" value="afternoon" />
            </el-select>
            <label>휴가유형</label>
            <el-select v-model="correction.form.leave_type" style="width: 100%">
              <el-option v-for="t in leaveTypes" :key="t.id" :label="t.name" :value="t.id" />
            </el-select>
          </div>
        </template>

        <el-input v-model="correction.reason" type="textarea" :rows="2"
          placeholder="정정 사유 (필수) — 예: 실제 사용일 8/10~8/11인데 8/12까지로 오기입" />
      </div>
      <template #footer>
        <el-button @click="correction.visible = false">닫기</el-button>
        <el-button type="warning" :loading="correction.saving" @click="submitCorrection">
          {{ correction.mode === 'cancel' ? '휴가 취소' : '정정 저장' }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'
import LeaveAdminSectionNav from '@/components/LeaveAdminSectionNav.vue'
import { useUserStore } from '@/stores/user'

const userStore = useUserStore()
// 관리자 정정 — 정정관리자 그룹(슈퍼유저 포함)만 노출
const canCorrect = computed(() => userStore.hasGroup('정정관리자'))

const currentYear = new Date().getFullYear()
const loading = ref(false)
const rows = ref([])
const leaveTypes = ref([])

const filters = ref({
  year: currentYear,
  leave_type: '',
  approval_status: '',
  has_attachment: '',
  requires_attachment_only: false,
  search: '',
})

const normalizeListResponse = (data) => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  return []
}

const extractApiErrorMessage = (err, fallback) => {
  const data = err?.response?.data
  if (!data) return fallback
  if (typeof data === 'string') return data
  if (data.detail) return data.detail
  const firstKey = Object.keys(data)[0]
  if (!firstKey) return fallback
  const value = data[firstKey]
  return Array.isArray(value) ? value[0] : value || fallback
}

const statusLabel = (s) => ({
  approved: '승인',
  pending: '대기',
  rejected: '반려',
  canceled: '취소',
}[s] ?? s)

const statusTagType = (s) => ({
  approved: 'success',
  pending: 'warning',
  rejected: 'danger',
}[s] ?? 'info')

// 증빙 필수 여부는 서버 정책(LeaveType.requires_attachment)을 유형 목록에서 룩업한다
const proofRequiredTypeIds = computed(
  () => new Set(leaveTypes.value.filter(t => t.requires_attachment).map(t => t.id))
)
const isProofRequired = (row) => proofRequiredTypeIds.value.has(row.leave_type)

const missingProofCount = computed(
  () => rows.value.filter(r => isProofRequired(r) && !(r.attachments || []).length).length
)

const fetchLeaveTypes = async () => {
  try {
    const { data } = await axiosInstance.get('leave-types/')
    leaveTypes.value = normalizeListResponse(data)
  } catch (err) {
    ElMessage.error(extractApiErrorMessage(err, '휴가유형을 불러오지 못했습니다.'))
  }
}

const fetchRows = async () => {
  loading.value = true
  try {
    const params = { year: filters.value.year }
    if (filters.value.leave_type) params.leave_type = filters.value.leave_type
    if (filters.value.approval_status) params.approval_status = filters.value.approval_status
    if (filters.value.has_attachment !== '') params.has_attachment = filters.value.has_attachment
    if (filters.value.requires_attachment_only) params.requires_attachment = 1
    if (filters.value.search?.trim()) params.search = filters.value.search.trim()

    const { data } = await axiosInstance.get('leaves/admin-requests/', { params })
    rows.value = normalizeListResponse(data)
  } catch (err) {
    ElMessage.error(extractApiErrorMessage(err, '신청내역을 불러오지 못했습니다.'))
    rows.value = []
  } finally {
    loading.value = false
  }
}

// ── 관리자 정정 (2026-08-03) ──
const correction = ref({
  visible: false, saving: false, mode: 'edit', reason: '',
  row: null,
  form: { start_date: '', end_date: '', days_requested: 1, session: 'full', leave_type: '' },
})

const openCorrection = (row) => {
  correction.value = {
    visible: true, saving: false, mode: 'edit', reason: '',
    row,
    form: {
      start_date: row.start_date,
      end_date: row.end_date,
      days_requested: row.days_requested,
      session: row.session || 'full',
      leave_type: row.leave_type,
    },
  }
}

const submitCorrection = async () => {
  const c = correction.value
  if (!c.reason.trim()) {
    ElMessage.warning('정정 사유를 입력하세요.')
    return
  }
  c.saving = true
  try {
    const body = c.mode === 'cancel'
      ? { reason: c.reason, cancel: true }
      : { reason: c.reason, ...c.form }
    const { data } = await axiosInstance.post(`leaves/${c.row.id}/correct/`, body)
    ;(data.warnings || []).forEach(w => ElMessage.warning({ message: w, duration: 6000 }))
    ElMessage.success(data.message || '정정되었습니다.')
    c.visible = false
    await fetchRows()
  } catch (err) {
    ElMessage.error(extractApiErrorMessage(err, '정정에 실패했습니다.'))
  } finally {
    c.saving = false
  }
}

onMounted(async () => {
  await fetchLeaveTypes()
  // 업무팀 주 용도가 증빙 확인이라 기본값을 '증빙 필수 유형만'으로 둔다
  filters.value.requires_attachment_only = true
  await fetchRows()
})
</script>

<style scoped>
.att-links {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.att-link {
  color: #409eff;
  text-decoration: none;
  font-size: 13px;
  word-break: break-all;
}
.att-link:hover {
  text-decoration: underline;
}
.missing-warn {
  color: #f56c6c;
  font-weight: 600;
}
.corr-grid {
  display: grid;
  grid-template-columns: 70px 1fr;
  gap: 8px 10px;
  align-items: center;
  margin-bottom: 10px;
}
.corr-grid label {
  font-size: 13px;
  color: #606266;
}
</style>
